import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { fetchCenterDetailsQueryOptions } from '@avanta/shared/hooks/useFetchCenterDetails';
import { fetchPsychologistDetailsQueryOptions } from '@avanta/shared/hooks/useFetchPsychologistDetails';
import { mapBookedContactMethod } from '@avanta/shared/utils/appointments';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { AppointmentService } from 'services/appointments';
import { type Appointment } from 'types/appointments';

export const useFetchAppointmentsHistory = (patientId: string): UseQueryResult<Appointment[]> => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QUERY_KEYS.FETCH_APPOINTMENTS_HISTORY, patientId],
    queryFn: async () => {
      const { data: appointments } = await AppointmentService.getAppointments(patientId, {
        includePast: true,
        includeCancelled: true,
      });

      const appointmentResults = await Promise.allSettled(
        appointments.map(async appointment => {
          const [psychologist, center] = await Promise.all([
            queryClient.fetchQuery(
              fetchPsychologistDetailsQueryOptions(appointment.psychologistId)
            ),
            queryClient.fetchQuery(fetchCenterDetailsQueryOptions(appointment.backofficeCenterId)),
          ]);

          return {
            ...appointment,
            psychologist: { psychologist },
            bookedContactMethod: mapBookedContactMethod(
              appointment.bookedContactMethod,
              appointment.backofficeCenterId
            ),
            center,
          };
        })
      );

      return appointmentResults.flatMap(result =>
        result.status === 'fulfilled' ? [result.value] : []
      );
    },
    enabled: !!patientId,
  });
};
